import React, { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Row, Col, Card, Table, Button, Badge, Modal, Form } from "react-bootstrap";
import PageTitle from "../../Components/PageTitle";
import RoundedPagination from "../../Components/Paginate";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { logout } from "../../redux/actions/AuthActions";
import useDebounce from "../../hooks/useDebounce";
import Preloader from "../../Components/Preloader";
import { getPendingCaseDetailsList, updatePendingDocument } from "../../redux/actions/CaseDetailsActions";
import { docUrl } from "../../axios";
import DocumentViewerModal from "../../Components/DocumentViewerModal";

const PendingDocument = ({ case_url, title }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    AuthReducer: { auth, errors },
    caseDetailsReducer: {
      caseDocumentDetailsPermissions,
      caseDocumentDetailsLists,
      loadingCaseDetails,
      caseDocumentDetailsCount,
    },
  } = useSelector((state) => state);
  const [page, setPage] = useState(1);
  const [paginate, setPaginate] = useState(10);
  const [search] = useState("");
  const [sort] = useState('desc');
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState({ url: '', fileName: '' });
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedDocumentForReject, setSelectedDocumentForReject] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const fetchCaseDetailsList = useCallback(() => {
    if (auth?.token) {
      dispatch(getPendingCaseDetailsList(auth.token, paginate, page, search, sort));
    }
  }, [auth, paginate, page, search, sort, dispatch]);

  useDebounce(fetchCaseDetailsList, 500, [auth, paginate, page, search, sort]);


  useEffect(() => {
    if (errors) {
      if (errors?.status === 401) {
        dispatch(logout())
        toast.error(errors?.data?.message);
        navigate('/login');
        window.location.reload();
      }
    }
  }, [errors, dispatch, navigate])


  const handlePaginateChange = (e) => {
    const value = e.target.value === "All" ? caseDocumentDetailsCount : parseInt(e.target.value);
    setPaginate(value);
    setPage(1);
  };

  const handleDocumentClick = (documentUrl, fileName) => {
    setSelectedDocument({ url: documentUrl, fileName: fileName });
    setShowDocumentModal(true);
  };

  const hideDocumentModal = () => {
    setShowDocumentModal(false);
    setSelectedDocument({ url: '', fileName: '' });
  };

  const handleApprove = async (documentId) => {
    try {
      await dispatch(updatePendingDocument(auth.token, documentId, 'Accept', ''));
      toast.success('Document approved successfully');
      fetchCaseDetailsList(); // Refresh the list
    } catch (error) {
      toast.error('Failed to approve document');
    }
  };

  const handleRejectClick = (documentId) => {
    setSelectedDocumentForReject(documentId);
    setShowRejectModal(true);
  };

  const handleRejectSubmit = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    try {
      await dispatch(updatePendingDocument(auth.token, selectedDocumentForReject, 'Reject', rejectReason));
      toast.success('Document rejected successfully');
      setShowRejectModal(false);
      setRejectReason('');
      setSelectedDocumentForReject(null);
      fetchCaseDetailsList(); // Refresh the list
    } catch (error) {
      toast.error('Failed to reject document');
    }
  };

  const handleRejectCancel = () => {
    setShowRejectModal(false);
    setRejectReason('');
    setSelectedDocumentForReject(null);
  };

  useEffect(() => {
    document.title = `Pending Document :: ${auth?.generalSettings?.title}`;
  }, [auth?.generalSettings?.title]);

  return (
    <>
      <PageTitle
        breadCrumbItems={[
          { label: "Pending Document", path: "Pending-Document", active: true },
        ]}
        title={"Pending Document"}
      />

      <Row>
        <Col>
          <Card>
            <Card.Body>
              <div className="mb-2">
                <Row>
                  <Col lg={3}>
                  </Col>
                  <Col lg={9}>
                    <div className="d-flex justify-content-end align-items-center">
                      <div className="button-list">
                        {(caseDocumentDetailsPermissions.print) && (<>
                          <Button variant="dark">
                            <i className="mdi mdi-printer me-1"></i>Print
                          </Button>
                        </>)}
                        {(caseDocumentDetailsPermissions.excel) && (<>
                          <Button variant="info">
                            <i className="mdi mdi-file-excel me-1"></i>Excel
                          </Button>
                        </>)}
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>

              <div className="table-responsive" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '500px', border: '1px solid #dee2e6' }}>
                <Table
                  className="mb-0 table-bordered"
                  size="sm"
                  striped
                  style={{ width: 'max-content', minWidth: '100%', tableLayout: 'auto', whiteSpace: 'nowrap' }}
                >
                  <thead style={{ position: "sticky", top: 0, backgroundColor: "#f8f9fa", zIndex: 1 }}>
                    <tr>
                      <th className="py-1 px-2 text-center align-middle">SR No.</th>
                      <th className="py-1 px-2 text-center align-middle">Claim/Policy</th>
                      <th className="py-1 px-2 text-center align-middle">Insurance</th>
                      <th className="py-1 px-2 text-center align-middle">Hospital</th>
                      <th className="py-1 px-2 text-center align-middle">Document Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {caseDocumentDetailsLists.length === 0 ? (
                      <tr>
                        <td colSpan="5">No data found</td>
                      </tr>
                    ) : (
                      <>
                        {caseDocumentDetailsLists?.map?.((item, index) => {
                          return (
                            <tr
                              key={item?.id}
                            >
                              <th scope="row">{index + 1 + (page - 1) * paginate}</th>
                              <td className="py-1 px-2 align-middle" style={{ fontSize: '0.8rem' }}>
                                Claim: {item?.claim_number}<br />
                                Policy: {item?.policy_number}<br />
                                Type: {item?.claimTypeCaseDetails?.name}
                              </td>
                              <td className="py-1 px-2 align-middle" style={{ fontSize: '0.8rem' }}>{item?.insuranceIdCaseDetails?.name}</td>
                              <td className="py-1 px-2 align-middle" style={{ whiteSpace: "normal", wordBreak: "break-all", fontSize: '0.8rem', width: '200px' }}>{item?.hospitaldCaseDetails?.hospital_name}</td>
                              <td>
                                {item?.caseIdCaseFieldOfficerAllocation.length > 0 && (
                                  <Table size="sm" className="mb-0">
                                    <thead className="thead-light">
                                      <tr>
                                        <th className="py-1 px-2 text-center align-middle" style={{ fontSize: '0.8rem' }}>Investigator Name</th>
                                        <th className="py-1 px-2 text-center align-middle" style={{ fontSize: '0.8rem' }}>Document</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {item?.caseIdCaseFieldOfficerAllocation?.map((value, index) => (
                                        <tr key={index}>
                                          <td className="py-1 px-2 align-middle" style={{ fontSize: '0.8rem' }}>
                                            {value?.foIdCaseFieldOfficerAllocation?.name} ({value?.foIdCaseFieldOfficerAllocation?.adminUserType?.name})
                                          </td>
                                          <td className="py-1 px-2" style={{ fontSize: '0.8rem' }}>
                                            <Table size="sm" className="mb-0">
                                              <thead className="thead-light">
                                                <tr>
                                                  <th className="py-1 px-2 text-center align-middle" style={{ fontSize: '0.8rem' }}>Document Name</th>
                                                  <th className="py-1 px-2 text-center align-middle" style={{ fontSize: '0.8rem' }}>Document</th>
                                                  <th className="py-1 px-2 text-center align-middle" style={{ fontSize: '0.8rem' }}>Status</th>
                                                  <th className="py-1 px-2 text-center align-middle" style={{ fontSize: '0.8rem' }}>Action</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {value?.fieldOfficerCaseCheckListDocument?.map((valueDoc, index) => (
                                                  <tr key={index}>
                                                    <td className="py-1 px-2 align-middle" style={{ whiteSpace: "normal", wordBreak: "break-all", fontSize: '0.8rem', width: '200px' }}>
                                                      {`${valueDoc?.subCheckListCaseCheckListDocument?.checklist_subchecklist?.name} - ${valueDoc?.subCheckListCaseCheckListDocument?.name}`}
                                                    </td>
                                                    <td className="py-1 px-2 text-center align-middle" style={{ fontWeight: 600, whiteSpace: "normal", wordBreak: "break-all", fontSize: '0.8rem', width: '200px' }}>
                                                      {(() => {
                                                        try {
                                                          const parsedDocs = typeof valueDoc?.upload_document === 'string'
                                                            ? JSON.parse(valueDoc.upload_document)
                                                            : valueDoc?.upload_document;

                                                          return parsedDocs?.length > 0 &&
                                                            parsedDocs.map((doc, docIndex) => {
                                                              const documentUrl = `${docUrl}/uploads/document/${item.id}/checklist/${doc.customFilename || doc.file_name}`;
                                                              const fileName = doc.customFilename || doc.originalname || doc.file_name;
                                                              return (
                                                                <Badge
                                                                  key={docIndex}
                                                                  bg="success"
                                                                  className="me-1"
                                                                  style={{ cursor: 'pointer' }}
                                                                  onClick={() => handleDocumentClick(documentUrl, fileName)}
                                                                >
                                                                  {fileName}
                                                                </Badge>
                                                              );
                                                            });
                                                        } catch (error) {
                                                          console.error('Error parsing upload_document:', error);
                                                          return valueDoc?.upload_document?.length > 0 &&
                                                            valueDoc?.upload_document.map((doc, docIndex) => {
                                                              const documentUrl = `${docUrl}/uploads/document/${item.id}/checklist/${doc.customFilename || doc.file_name}`;
                                                              const fileName = doc.file_name;
                                                              return (
                                                                <Badge
                                                                  key={docIndex}
                                                                  bg="success"
                                                                  className="me-1"
                                                                  style={{ cursor: 'pointer' }}
                                                                  onClick={() => handleDocumentClick(documentUrl, fileName)}
                                                                >
                                                                  {fileName}
                                                                </Badge>
                                                              );
                                                            });
                                                        }
                                                      })()}
                                                    </td>
                                                    <td className="py-1 px-2 align-middle" style={{ fontSize: '0.8rem' }}>
                                                      {valueDoc?.status}
                                                    </td>
                                                    <td className="py-1 px-2 align-middle" style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                                                      <div className="d-flex gap-1 flex-wrap">
                                                        <Button
                                                          variant="success"
                                                          size="sm"
                                                          style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                                          onClick={() => handleApprove(valueDoc.id)}
                                                        >
                                                          <i className="mdi mdi-check me-1"></i>Approve
                                                        </Button>
                                                        <Button
                                                          variant="danger"
                                                          size="sm"
                                                          style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                                                          onClick={() => handleRejectClick(valueDoc.id)}
                                                        >
                                                          <i className="mdi mdi-close me-1"></i>Reject
                                                        </Button>
                                                      </div>
                                                    </td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </Table>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </Table>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </>
                    )}
                  </tbody>
                </Table>
              </div>
              <div class="mt-3 d-lg-flex align-items-center text-center pb-1">
                <div class="d-inline-block me-3">
                  <label class="me-1">Display :</label>
                  <select class="form-select d-inline-block w-auto" onChange={handlePaginateChange}>
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>
                <span class="me-3">Page <strong>{page}</strong> of <strong>{Math.ceil(caseDocumentDetailsCount / paginate)}</strong> </span>
                <RoundedPagination currentPage={page} totalPages={Math.ceil(caseDocumentDetailsCount / paginate)} onPageChange={(pageNumber) => setPage(pageNumber)} />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <DocumentViewerModal
        show={showDocumentModal}
        onHide={hideDocumentModal}
        documentUrl={selectedDocument.url}
        fileName={selectedDocument.fileName}
      />

      {/* Rejection Reason Modal */}
      <Modal show={showRejectModal} onHide={handleRejectCancel} centered>
        <Modal.Header closeButton>
          <Modal.Title>Reject Document</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Reason for Rejection *</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Please provide a reason for rejecting this document..."
                required
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleRejectCancel}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleRejectSubmit}>
            Reject Document
          </Button>
        </Modal.Footer>
      </Modal>

      {loadingCaseDetails && (<Preloader />)}
    </>
  );
};

export default PendingDocument;
